import React, { useState, useContext, useEffect, useMemo } from 'react'
import Image from 'next/image'
import { LocationContext } from '../../../context/LocationContext'
import { RequestContext } from '../../../context/RequestContext'
import { downloadServiceProviders } from '../../../api/service-provider/sp-list'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Sparkles } from 'lucide-react'

/**
 * Trainer matching loading screen with trainer photo juggling animation.
 * Extracted from TrainerMatch's LoadingScreen.
 * Fetches trainers via API, shows photo animation, then auto-transitions via onComplete.
 */
export const PtTrainerMatchLoader = ({ onComplete, setMatchedTrainer }) => {
    const [isLoading, setIsLoading] = useState(true)
    const [minTimeElapsed, setMinTimeElapsed] = useState(false)
    const [trainerPool, setTrainerPool] = useState([])
    const [activeIndex, setActiveIndex] = useState(0)

    const { countryName, userLatitude, userLongitude } = useContext(LocationContext)
    const { requestCriteria } = useContext(RequestContext)

    // Extract trainer gender preference
    const trainerGenderPref = useMemo(() => {
        if (!requestCriteria || !Array.isArray(requestCriteria)) return null
        for (let i = 0; i < requestCriteria.length; i++) {
            const item = requestCriteria[i]
            if (item && item["4"] !== undefined && item["4"] !== null) return item["4"]
        }
        return null
    }, [requestCriteria])

    // Minimum 3-second loading display
    useEffect(() => {
        const timer = setTimeout(() => setMinTimeElapsed(true), 3000)
        return () => clearTimeout(timer)
    }, [])

    // Fetch trainers
    useEffect(() => {
        const fetchTrainers = async () => {
            setIsLoading(true)
            try {
                const spList = await downloadServiceProviders(
                    "Fitness Trainer", 0, 10, countryName, userLatitude, userLongitude, 50, "kms", true, true, false
                )
                if (spList && spList.length > 0) {
                    setTrainerPool(spList)
                }
            } catch (err) {
                console.error("Error fetching trainers:", err)
            }
            setIsLoading(false)
        }
        if (userLatitude && userLongitude && countryName) fetchTrainers()
    }, [userLatitude, userLongitude, countryName])

    // Auto-transition when both loading done and min time elapsed
    useEffect(() => {
        if (!isLoading && minTimeElapsed && onComplete) {
            onComplete(trainerPool, trainerGenderPref)
            if (setMatchedTrainer && trainerPool.length > 0) {
                setMatchedTrainer(trainerPool[0])
            }
        }
    }, [isLoading, minTimeElapsed, onComplete, trainerPool, trainerGenderPref, setMatchedTrainer])

    // Cycle through trainer photos
    useEffect(() => {
        if (trainerPool.length <= 1) return
        const interval = setInterval(() => {
            setActiveIndex(prev => (prev + 1) % trainerPool.length)
        }, 600)
        return () => clearInterval(interval)
    }, [trainerPool.length])

    const hasPhotos = trainerPool.length > 0

    // Show 5 photos in orbit
    const displayPool = useMemo(() => {
        if (trainerPool.length <= 5) return trainerPool
        const start = (Math.floor(activeIndex / 5) * 5) % trainerPool.length
        const pool = []
        for (let i = 0; i < 5; i++) {
            pool.push(trainerPool[(start + i) % trainerPool.length])
        }
        return pool
    }, [trainerPool, activeIndex])

    // Calculate position for small orbiting photos
    const getOrbitPosition = (index, total) => {
        const angle = ((360 / total) * index - 90) * (Math.PI / 180)
        const radius = 72
        return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius }
    }

    return (
        <div className='min-h-140 relative'>
            <div className="absolute h-fit items-center left-0 right-0 top-0 bottom-0 m-auto max-w-lg mx-auto px-3 py-10 text-center">
                <div className="relative mx-auto mb-6" style={{ width: 200, height: 200 }}>
                    {/* Pulsing outer rings */}
                    <motion.div
                        className="absolute inset-0 rounded-full bg-linear-to-br from-orange-500/30 to-amber-500/30"
                        animate={{ scale: [1, 1.25, 1], opacity: [0.3, 0.05, 0.3] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <motion.div
                        className="absolute inset-3 rounded-full bg-linear-to-br from-orange-500/40 to-amber-500/40"
                        animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.1, 0.4] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                    />

                    {hasPhotos ? (
                        <>
                            {/* Rotating dashed scan ring */}
                            <motion.div
                                className="absolute rounded-full"
                                style={{ inset: 26, border: '2px dashed rgba(249,115,22,0.35)' }}
                                animate={{ rotate: 360 }}
                                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                            />

                            {/* Small trainer photos in orbit */}
                            {displayPool.map((sp, i) => {
                                const pos = getOrbitPosition(i, displayPool.length)
                                const isActive = activeIndex % displayPool.length === i
                                return (
                                    <motion.div
                                        key={i}
                                        className={`absolute rounded-full overflow-hidden shadow-md ${isActive ? 'ring-2 ring-orange-400 z-10' : 'ring-1 ring-white/80'}`}
                                        style={{
                                            width: 44, height: 44,
                                            top: '50%', left: '50%',
                                            marginTop: -22 + pos.y,
                                            marginLeft: -22 + pos.x
                                        }}
                                        animate={{
                                            scale: isActive ? 1.2 : 0.85,
                                            opacity: isActive ? 1 : 0.45
                                        }}
                                        transition={{ duration: 0.3, ease: "easeOut" }}
                                    >
                                        {sp.profilePic ? (
                                            <Image src={sp.profilePic} alt="" fill className="object-cover" sizes="44px" />
                                        ) : (
                                            <div className="w-full h-full bg-linear-to-br from-orange-400 to-amber-400 flex items-center justify-center">
                                                <span className="text-white text-sm font-bold">{(sp.userFirstName || "T").charAt(0)}</span>
                                            </div>
                                        )}
                                    </motion.div>
                                )
                            })}

                            {/* Center cycling spotlight photo */}
                            <div className="absolute inset-0 flex items-center justify-center z-20">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeIndex}
                                        className="relative w-20 h-20 rounded-full overflow-hidden shadow-2xl ring-2 ring-orange-500"
                                        initial={{ scale: 0.5, opacity: 0, y: 15 }}
                                        animate={{ scale: 1, opacity: 1, y: 0 }}
                                        exit={{ scale: 0.7, opacity: 0, y: -15 }}
                                        transition={{ duration: 0.25, ease: "easeOut" }}
                                    >
                                        {trainerPool[activeIndex]?.profilePic ? (
                                            <Image
                                                src={trainerPool[activeIndex].profilePic}
                                                alt=""
                                                fill
                                                className="object-cover"
                                                sizes="80px"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-linear-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                                                <span className="text-white font-bold text-xl">
                                                    {(trainerPool[activeIndex]?.userFirstName || "T").charAt(0)}
                                                </span>
                                            </div>
                                        )}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </>
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <motion.div
                                className="relative w-16 h-16 rounded-full bg-linear-to-br from-orange-500 to-amber-500 shadow-xl flex items-center justify-center"
                                animate={{ rotate: [0, 5, -5, 0] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <motion.div
                                    animate={{ opacity: [1, 0, 0, 1], scale: [1, 0.8, 0.8, 1] }}
                                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <Search className="w-7 h-7 text-white" />
                                </motion.div>
                                <motion.div
                                    className="absolute"
                                    animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1, 1, 0.8] }}
                                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <Sparkles className="w-7 h-7 text-white" />
                                </motion.div>
                            </motion.div>
                        </div>
                    )}
                </div>

                {/* Progress dots */}
                {hasPhotos && (
                    <div className="flex justify-center gap-1.5 mb-3">
                        {displayPool.map((_, i) => (
                            <motion.div
                                key={i}
                                className={`w-1.5 h-1.5 rounded-full ${activeIndex % displayPool.length === i ? 'bg-orange-600' : 'bg-orange-600/30'}`}
                                animate={activeIndex % displayPool.length === i ? { scale: [1, 1.6, 1] } : { scale: 1 }}
                                transition={{ duration: 0.3 }}
                            />
                        ))}
                    </div>
                )}

                <h2 className="text-lg font-bold text-orange-600 mb-1">
                    {hasPhotos ? "Finding your best match..." : "Matching you with a trainer..."}
                </h2>
                <p className="text-gray-500 text-sm">
                    {hasPhotos ? "Scanning our certified StringsSG trainers near you" : "Finding our best trainer near you"}
                </p>
            </div>
        </div>
    )
}
